import type { DriverRow } from "@/types/driver";
import { zinc } from "@/ui/tokens";
import { greenColor, redColor, yellowColor } from "@/utils/color";
import { isWorkspaceMembershipOffline } from "@/utils/workspaceMembershipOffline";

/**
 * Whether the hands-free heartbeat is missing or stale (unreachable via HF).
 *
 * Delegates to `isWorkspaceMembershipOffline` so the threshold stays aligned with
 * `offlineThreshold` / `sqlMembershipIsOffline` and workspace status transforms.
 */
export function isHandsFreeHeartbeatOffline(handsFreeUpdatedAt: string | Date | null | undefined): boolean {
  return isWorkspaceMembershipOffline({ handsFreeUpdatedAt });
}

/**
 * Whether a driver reads as never logged in: their membership is in the never-logged-in set and their hands-free
 * heartbeat is missing or stale.
 *
 * A fresh heartbeat outranks the set. Heartbeats only reach us over an authenticated device session, so one proves the
 * driver has logged in, whereas the set is REST-fetched and lags that login.
 *
 * @param unactivated - Whether the membership is in the never-logged-in set (`useUnactivatedMembers`).
 * @param handsFreeUpdatedAt - When the driver's hands-free status last changed.
 */
export function isNeverLoggedIn(unactivated: boolean, handsFreeUpdatedAt: string | Date | null | undefined): boolean {
  return unactivated && isHandsFreeHeartbeatOffline(handsFreeUpdatedAt);
}

export type HandsFreeAvailabilityDisplay = {
  label: string;
  tooltip: string;
  color: string;
};

type AvailabilitySource = Pick<
  DriverRow,
  | "calculatedAvailability"
  | "mostRecentHandsFreeStatusEnabled"
  | "mostRecentVolumeAudible"
  | "mostRecentHandsFreeStatusUpdatedAt"
>;

/**
 * Every work-device state the column can display, in the order the header legend lists them.
 *
 * The legend documents exactly what the cells render, so both read from here rather than restating the
 * dispatcher-facing wording.
 */
export const HANDS_FREE_DISPLAYS = {
  available: {
    label: "Available",
    tooltip: "Driver is in the cab and hands-free is ON",
    color: greenColor,
  },
  handsFreeOn: {
    label: "Hands-free ON",
    tooltip: "Driver has hands-free ON but may not be in the cab",
    color: yellowColor,
  },
  handsFreeOff: {
    label: "Hands-free OFF",
    tooltip: "Driver has hands-free OFF",
    color: redColor,
  },
  deviceMuted: {
    label: "Device muted",
    tooltip: "Driver has hands-free ON but their device is muted",
    color: redColor,
  },
} as const satisfies Record<string, HandsFreeAvailabilityDisplay>;

/**
 * Unactivated memberships (no account, or account with no Login event). Lighter zinc than Offline
 * so the two muted statuses stay distinguishable in the cell and filter chips.
 */
export const NEVER_LOGGED_IN_DISPLAY: HandsFreeAvailabilityDisplay = {
  label: "Never logged in",
  tooltip: "Driver has never logged into Storyboard",
  color: zinc[400],
};

/**
 * Missing or stale hands-free heartbeat. Same zinc as the Offline filter chip historically used
 * (`zinc[500]`) so cell, legend, and filter agree.
 */
export const OFFLINE_DISPLAY: HandsFreeAvailabilityDisplay = {
  label: "Offline",
  tooltip: "Driver's work device is unreachable",
  color: zinc[500],
};

/**
 * Maps backend availability + hands-free + mute state to the dispatcher-facing
 * label and tooltip used in the DriverView "Driver Availability" column.
 *
 * Precedence lives here alone: never-logged-in (only while the heartbeat is missing or stale) → offline →
 * work-device state.
 *
 * A fresh heartbeat outranks `neverLoggedIn`. Heartbeats only reach us over an authenticated device session, so
 * one proves the driver has logged in, whereas the unactivated set behind the flag is REST-fetched and lags that
 * login. Requiring the heartbeat to be *stale* keeps a genuinely unactivated membership labelled correctly even
 * when it carries a historical availability row (e.g. its account was later disconnected).
 *
 * This gate is not what keeps the label right long-term: DriverView refetches the lagging set the moment such
 * a heartbeat appears (see `useUnactivatedMembers`), clearing `neverLoggedIn` at the source while the heartbeat
 * is still fresh — well before it could go stale again and regress the label to "Never logged in".
 *
 * `Unavailable` (`notPresent`) is split into displays:
 *   - hands-free OFF → "Hands-free OFF" (precedence over mute)
 *   - hands-free ON and volume explicitly inaudible → "Device muted"
 *   - otherwise → "Hands-free ON" (same display as availability `unknown`)
 */
export function formatHandsFreeAvailability(
  driver: AvailabilitySource,
  opts?: { neverLoggedIn?: boolean },
): HandsFreeAvailabilityDisplay | null {
  if (isNeverLoggedIn(opts?.neverLoggedIn ?? false, driver.mostRecentHandsFreeStatusUpdatedAt)) {
    return NEVER_LOGGED_IN_DISPLAY;
  }
  if (isHandsFreeHeartbeatOffline(driver.mostRecentHandsFreeStatusUpdatedAt)) return OFFLINE_DISPLAY;

  switch (driver.calculatedAvailability) {
    case "present":
      return HANDS_FREE_DISPLAYS.available;
    case "unknown":
      return HANDS_FREE_DISPLAYS.handsFreeOn;
    case "notPresent":
      if (driver.mostRecentHandsFreeStatusEnabled === false) return HANDS_FREE_DISPLAYS.handsFreeOff;
      if (driver.mostRecentHandsFreeStatusEnabled === true && driver.mostRecentVolumeAudible === false) {
        return HANDS_FREE_DISPLAYS.deviceMuted;
      }
      return HANDS_FREE_DISPLAYS.handsFreeOn;
    default:
      return null;
  }
}
